import dayjs from 'dayjs'
import { getLocalDb } from '../client'
import { OP_STATUS, STORES } from '../constants'
import { AUTO_RETRY_ERROR_TYPES } from '../resolutionPolicy'

/**
 * Antrean transaksi async (Mode Kasir Zero-Loading; port dari kledopos-mobile
 * `repositories/asyncOps.ts`). Satu record per op:
 * `{ op_id, local_id, trans_type_id, action, ref_id, payload, batch_id, status,
 *    trans_id, error, error_type, error_payload, resolutions, attempts, next_attempt_at,
 *    user_id, endpoint, created_at, updated_at }`.
 * `error` = teks tampilan; `error_type`/`error_payload` = `error[0]` dari BE (konteks
 * form resolusi); `resolutions` = riwayat resolusi dari `/failed?include_history=1`.
 *
 * Khusus web: browser bisa dipakai bergantian beberapa kasir/tenant → setiap op
 * menyimpan `user_id` + `endpoint` pembuatnya, dan semua query antrean di-scope ke
 * pemilik aktif (`owner`) supaya op kasir/tenant lain tak ikut terkirim/terhitung.
 */

/** Retry backoff: eksponensial 30s→1m→2m→4m, mentok 30m; FAILED setelah 5x gagal. */
export const MAX_ATTEMPTS = 5
const RETRY_BASE_MS = 30_000
const RETRY_MAX_MS = 30 * 60_000

/**
 * Re-poll batch 'sent' bertingkat: cepat di awal (op biasanya hanya antre beberapa
 * detik di BE), lalu melambat sampai 60 dtk untuk op lama/nyangkut — supaya tidak
 * membebani server. Hitungan re-poll per batch di memori (reset saat reload tab).
 */
const SENT_REPOLL_BASE_MS = 4_000
const SENT_REPOLL_MAX_MS = 60_000
const sentRepollAttempts = new Map()

const sentRepollDelay = (attempt) =>
  Math.min(SENT_REPOLL_BASE_MS * 2 ** (attempt - 1), SENT_REPOLL_MAX_MS)

export const backoffMs = (attempts) => Math.min(RETRY_BASE_MS * 2 ** (attempts - 1), RETRY_MAX_MS)

// Tanpa pemilik (belum login) → tak ada op yang dianggap milik siapa pun.
const isOwnedBy = (op, owner) =>
  !!owner &&
  String(op.user_id ?? '') === String(owner.userId ?? '') &&
  String(op.endpoint ?? '') === String(owner.endpoint ?? '')

const byCreatedAsc = (a, b) => (a.created_at ?? 0) - (b.created_at ?? 0)

const getByStatus = async (status) => {
  const db = await getLocalDb()
  return db.getAllFromIndex(STORES.ASYNC_OPS, 'status', status)
}

/** Ubah beberapa op dalam satu transaksi; `patch(op)` mengembalikan field baru. */
const updateOps = async (opIds, patch) => {
  if (!opIds?.length) {
    return 0
  }
  const db = await getLocalDb()
  const tx = db.transaction(STORES.ASYNC_OPS, 'readwrite')
  let changed = 0
  await Promise.all(
    opIds.map(async (opId) => {
      const op = await tx.store.get(opId)
      if (op) {
        changed += 1
        await tx.store.put({ ...op, ...patch(op), updated_at: Date.now() })
      }
    }),
  )
  await tx.done
  return changed
}

/** Tulis op baru ke antrean (status pending). */
export const enqueueOp = async (op) => {
  const db = await getLocalDb()
  const now = Date.now()
  await db.put(STORES.ASYNC_OPS, {
    op_id: op.op_id,
    local_id: op.local_id,
    trans_type_id: op.trans_type_id,
    action: op.action,
    ref_id: op.ref_id ?? null,
    payload: op.payload ?? {},
    batch_id: null,
    status: OP_STATUS.PENDING,
    trans_id: null,
    error: null,
    attempts: 0,
    next_attempt_at: null,
    user_id: op.user_id ?? null,
    endpoint: op.endpoint ?? null,
    created_at: now,
    updated_at: now,
  })
}

/**
 * Op pending yang SUDAH JATUH TEMPO (FIFO by created_at) milik `owner`; op yang
 * masih backoff dilewati — transaksi kasir independen, op yang sedang menunggu
 * jadwal ulang tidak boleh menahan transaksi berikutnya.
 */
export const getDuePendingOps = async (limit = 100, owner) => {
  const now = Date.now()
  const rows = await getByStatus(OP_STATUS.PENDING)
  return rows
    .filter((op) => isOwnedBy(op, owner))
    .filter((op) => op.next_attempt_at == null || op.next_attempt_at <= now)
    .sort(byCreatedAsc)
    .slice(0, limit)
}

/**
 * Catat kegagalan upload: naikkan attempts + jadwalkan retry dengan backoff.
 * Setelah MAX_ATTEMPTS → FAILED (berhenti auto-retry; muncul di Riwayat).
 */
export const recordBatchFailure = async (opIds, errText) => {
  const now = Date.now()
  await updateOps(opIds, (op) => {
    const attempts = (op.attempts ?? 0) + 1
    const failed = attempts >= MAX_ATTEMPTS
    return {
      attempts,
      status: failed ? OP_STATUS.FAILED : OP_STATUS.PENDING,
      error: errText,
      next_attempt_at: failed ? null : now + backoffMs(attempts),
    }
  })
}

/**
 * Tandai op FAILED sekarang tanpa backoff — untuk penolakan permanen BE
 * (400/403/422) yang percuma di-retry otomatis.
 */
export const markFailedNow = async (opIds, errText) => {
  await updateOps(opIds, (op) => ({
    attempts: (op.attempts ?? 0) + 1,
    status: OP_STATUS.FAILED,
    error: errText,
    next_attempt_at: null,
  }))
}

/** Tunda re-poll op 'sent' di batch ini (4s→8s→…→60s). */
export const deferSentBatch = async (batchId) => {
  const db = await getLocalDb()
  const ops = await db.getAllFromIndex(STORES.ASYNC_OPS, 'batch_id', batchId)
  const sentIds = ops.filter((op) => op.status === OP_STATUS.SENT).map((op) => op.op_id)
  if (!sentIds.length) {
    sentRepollAttempts.delete(batchId)
    return
  }
  const attempt = (sentRepollAttempts.get(batchId) ?? 0) + 1
  sentRepollAttempts.set(batchId, attempt)
  const nextAt = Date.now() + sentRepollDelay(attempt)
  await updateOps(sentIds, () => ({ next_attempt_at: nextAt }))
}

/**
 * Tandai op terkirim (setelah POST dikonfirmasi BE) + set batch_id. Op yang sudah
 * punya batch_id (dikirim ulang dgn op_id sama → `duplicated`) TETAP memakai batch
 * lamanya: di BE op itu milik batch lama, jadi rekonsiliasi harus ke sana — bila
 * ditimpa batch baru, rekonsiliasi tak pernah cocok & op nyangkut 'sent'.
 */
export const markSent = async (opIds, batchId) => {
  await updateOps(opIds, (op) => ({
    status: OP_STATUS.SENT,
    batch_id: op.batch_id ?? batchId,
    next_attempt_at: null,
  }))
}

/**
 * Error dari BE dianggap NYATA hanya bila ada isinya. BE mengembalikan `error: []`
 * / `{}` / '' untuk "tidak ada error".
 */
const hasMeaningfulError = (e) => {
  if (e == null) {
    return false
  }
  if (typeof e === 'string') {
    return e.trim().length > 0
  }
  if (Array.isArray(e)) {
    return e.length > 0
  }
  if (typeof e === 'object') {
    return Object.keys(e).length > 0
  }
  return true
}

/** Label fallback per `error_type` BE bila `message` kosong. */
const ERROR_TYPE_LABELS = {
  target_not_found: 'Transaksi tujuan tidak ditemukan di server',
  missing_product: 'Produk sudah tidak ada',
  missing_contact: 'Kontak sudah tidak ada',
  locked_trans_date: 'Tanggal transaksi berada di periode terkunci',
  shift_closed: 'Shift sudah ditutup',
  duplicate_transaction: 'Kemungkinan transaksi ganda',
  business_rule: 'Ditolak server',
  internal_error: 'Gangguan server',
}

/**
 * Ubah `error` BE (`[{error_type, message, payload}]`) jadi teks yang bisa dibaca
 * kasir; string dipakai apa adanya, bentuk lain di-JSON-kan.
 */
export const formatOpError = (e) => {
  if (typeof e === 'string') {
    return e
  }
  const list = Array.isArray(e) ? e : [e]
  const texts = list
    .map((item) =>
      typeof item === 'string'
        ? item
        : item?.message || ERROR_TYPE_LABELS[item?.error_type] || null,
    )
    .filter(Boolean)
  return texts.length ? texts.join('; ') : JSON.stringify(e)
}

/**
 * Error BE dalam bentuk tersimpan: teks tampilan + `error_type`/`payload` dari entri
 * pertama (BE mencatat tepat satu error per kegagalan).
 */
const toStoredError = (e) => {
  if (!hasMeaningfulError(e)) {
    return { error: null, error_type: null, error_payload: null }
  }
  const first = Array.isArray(e) ? e[0] : e
  const isObj = first != null && typeof first === 'object'
  return {
    error: formatOpError(e),
    error_type: isObj ? (first.error_type ?? null) : null,
    error_payload: isObj ? (first.payload ?? null) : null,
  }
}

/**
 * Status lokal dari hasil rekonsiliasi / daftar `/failed`. status_id BE: 1/2 antre/proses
 * (SENT), 3 done, 4 failed (FAILED) & 5 blocked (BLOCKED) — keduanya diselesaikan via
 * /resolve, 6 discarded (DISCARDED). Error non-kosong selain blocked = FAILED.
 * Mengembalikan `{status, error, error_type, error_payload}`.
 */
export const resolveReconcileStatus = (result) => {
  const err = toStoredError(result?.error)
  const statusId = Number(result?.status_id)
  if (statusId === 6) {
    return { status: OP_STATUS.DISCARDED, ...toStoredError(null) }
  }
  if (statusId === 5) {
    return { status: OP_STATUS.BLOCKED, ...err }
  }
  if (err.error != null || statusId === 4) {
    return { status: OP_STATUS.FAILED, ...err }
  }
  if (result?.trans_id != null || statusId === 3) {
    return { status: OP_STATUS.DONE, ...err }
  }
  return { status: OP_STATUS.SENT, ...err }
}

/** Terapkan hasil rekonsiliasi; cocokkan by op_id atau local_id. */
export const applyReconcile = async (results) => {
  if (!results?.length) {
    return
  }
  const db = await getLocalDb()
  const tx = db.transaction(STORES.ASYNC_OPS, 'readwrite')
  const all = results.some((r) => !r.op_id && r.local_id) ? await tx.store.getAll() : []
  await Promise.all(
    results.map(async (r) => {
      let op = r.op_id ? await tx.store.get(r.op_id) : null
      if (!op && r.local_id) {
        op = all.find((row) => row.local_id === r.local_id) ?? null
      }
      if (!op) {
        return
      }
      const next = resolveReconcileStatus(r)
      await tx.store.put({
        ...op,
        ...next,
        trans_id: r.trans_id ?? null,
        updated_at: Date.now(),
      })
    }),
  )
  await tx.done
}

/** batch_id yang masih 'sent' & sudah jatuh tempo di-poll ulang (milik `owner`). */
export const getSentBatchIds = async (owner) => {
  const now = Date.now()
  const rows = await getByStatus(OP_STATUS.SENT)
  const ids = rows
    .filter((op) => isOwnedBy(op, owner))
    .filter((op) => op.batch_id && (op.next_attempt_at == null || op.next_attempt_at <= now))
    .map((op) => op.batch_id)
  return [...new Set(ids)]
}

const isFinal = (op) => op.status === OP_STATUS.DONE || op.status === OP_STATUS.DISCARDED

/**
 * Op untuk Pusat Sinkronisasi milik `owner` (terbaru dulu): SEMUA op yang belum final
 * (pending/sent/gagal/terblokir — tak boleh tersembunyi oleh limit, karena menahan tutup
 * shift) ditambah op final (done/dibatalkan) terbaru sebanyak `finalLimit`.
 */
export const getOpsForSyncCenter = async (owner, finalLimit = 200) => {
  const db = await getLocalDb()
  const rows = (await db.getAll(STORES.ASYNC_OPS))
    .filter((op) => isOwnedBy(op, owner))
    .sort((a, b) => byCreatedAsc(b, a))
  const open = rows.filter((op) => !isFinal(op))
  const final = rows.filter(isFinal).slice(0, finalLimit)
  return [...open, ...final].sort((a, b) => byCreatedAsc(b, a))
}

/** Satu op by op_id (untuk menyegarkan panel detail / validasi koreksi). */
export const getOpById = async (opId) => {
  if (!opId) {
    return null
  }
  const db = await getLocalDb()
  return (await db.get(STORES.ASYNC_OPS, opId)) ?? null
}

/**
 * Ganti pesan error op tanpa mengubah status/error_type — untuk penolakan /resolve
 * (izin kurang, kapabilitas tak didukung) supaya kasir tahu sebabnya & pilihan aksinya
 * tetap berdasarkan error_type asli.
 */
export const setOpError = async (opId, errText) => {
  await updateOps([opId], () => ({ error: errText }))
}

/** Catat resolusi yang baru dikirim ke riwayat lokal op ("sudah dicoba N×"). */
export const appendOpResolution = async (opId, resolution) => {
  await updateOps([opId], (op) => ({ resolutions: [...(op.resolutions ?? []), resolution] }))
}

/** Op dibatalkan via resolve `discard` / batal lokal — final, tak memblokir tutup shift. */
export const markDiscarded = async (opId) => {
  await updateOps([opId], () => ({ status: OP_STATUS.DISCARDED, next_attempt_at: null }))
}

const toMillis = (v) => {
  const d = v ? dayjs(v) : null
  return d?.isValid() ? d.valueOf() : Date.now()
}

/**
 * Gabungkan daftar gagal/terblokir dari `/failed` ke IndexedDB (server = sumber kebenaran
 * status). Op yang sudah ada diperbarui (pemiliknya tetap); yang tak ada di lokal (sudah
 * ter-prune / data browser dihapus) disisipkan dengan `user_id` dari server + endpoint
 * aktif, supaya tetap bisa diselesaikan & tetap tersembunyi dari kasir lain.
 */
export const upsertServerFailedOps = async (ops, owner) => {
  if (!ops?.length || !owner) {
    return
  }
  const db = await getLocalDb()
  const tx = db.transaction(STORES.ASYNC_OPS, 'readwrite')
  const now = Date.now()
  await Promise.all(
    ops
      .filter((o) => o?.op_id)
      .map(async (o) => {
        const next = resolveReconcileStatus(o)
        const resolutions = Array.isArray(o.resolutions) ? o.resolutions : null
        const existing = await tx.store.get(o.op_id)
        if (existing) {
          await tx.store.put({
            ...existing,
            ...next,
            batch_id: existing.batch_id ?? o.batch_id ?? null,
            trans_id: o.trans_id ?? null,
            resolutions: resolutions ?? existing.resolutions ?? null,
            next_attempt_at: null,
            updated_at: now,
          })
          return
        }
        await tx.store.put({
          op_id: o.op_id,
          local_id: o.local_id ?? null,
          trans_type_id: Number(o.trans_type_id) || 0,
          action: o.action ?? 'create',
          ref_id: o.ref_id != null ? String(o.ref_id) : null,
          payload: o.payload ?? {},
          batch_id: o.batch_id ?? null,
          ...next,
          trans_id: o.trans_id ?? null,
          resolutions,
          attempts: 0,
          next_attempt_at: null,
          user_id: o.user_id ?? owner.userId,
          endpoint: owner.endpoint,
          created_at: toMillis(o.created_at),
          updated_at: now,
        })
      }),
  )
  await tx.done
}

/** Hapus op sukses/dibatalkan lama (>7 hari) supaya riwayat tetap ringkas. */
export const pruneDoneOps = async (maxAgeMs = 7 * 24 * 60 * 60 * 1000) => {
  const cutoff = Date.now() - maxAgeMs
  const db = await getLocalDb()
  const tx = db.transaction(STORES.ASYNC_OPS, 'readwrite')
  const rows = await tx.store.getAll()
  await Promise.all(
    rows
      .filter(
        (op) =>
          (op.status === OP_STATUS.DONE || op.status === OP_STATUS.DISCARDED) &&
          (op.updated_at ?? 0) < cutoff,
      )
      .map((op) => tx.store.delete(op.op_id)),
  )
  await tx.done
}

/**
 * Kembalikan op yang gagal UPLOAD (belum pernah diterima BE) ke pending agar
 * di-upload ulang. Op yang gagal di BE (punya batch_id) dikirim ulang via /resolve
 * — re-POST op_id yang sama hanya jadi `duplicated` & tak dijalankan ulang.
 */
export const resetOpToPending = async (opId) => {
  await updateOps([opId], () => ({
    status: OP_STATUS.PENDING,
    error: null,
    error_type: null,
    error_payload: null,
    attempts: 0,
    next_attempt_at: null,
  }))
}

/** Op gagal di BE yang sudah di-/resolve → kembali 'sent' & langsung di-poll ulang. */
export const markOpResent = async (opId) => {
  await updateOps([opId], () => ({
    status: OP_STATUS.SENT,
    error: null,
    error_type: null,
    error_payload: null,
    next_attempt_at: null,
  }))
}

/**
 * Op lokal gagal/terblokir milik `owner` (sudah diterima BE) yang TIDAK lagi ada di daftar
 * `/failed` server — sudah diselesaikan di tempat lain (device/back-office) → kembali
 * 'sent' & jatuh tempo poll, supaya status aslinya diambil dari `GET ?batch_id=`. Tanpa
 * ini op tsb tetap "gagal" selamanya & menahan tutup shift.
 */
export const requeueOpsMissingFromServer = async (serverOpIds, owner) => {
  const db = await getLocalDb()
  const stale = (await db.getAll(STORES.ASYNC_OPS))
    .filter(
      (op) =>
        isOwnedBy(op, owner) &&
        op.batch_id &&
        (op.status === OP_STATUS.FAILED || op.status === OP_STATUS.BLOCKED) &&
        !serverOpIds.has(op.op_id),
    )
    .map((op) => op.op_id)
  await updateOps(stale, () => ({
    status: OP_STATUS.SENT,
    error: null,
    error_type: null,
    error_payload: null,
    next_attempt_at: null,
  }))
  return stale.length
}

/**
 * Op gagal/terblokir di BE milik `owner` yang AMAN di-`retry_as_is` massal ("Sinkronkan
 * Sekarang"): hanya `internal_error` (hasil tak pernah tercatat) & `target_not_found`
 * (biasanya pulih sendiri). Tipe lain — terutama `duplicate_transaction` (bisa
 * double-book) — wajib diputuskan manusia di Pusat Sinkronisasi.
 */
export const getAutoRetryableOps = async (owner) => {
  const db = await getLocalDb()
  return (await db.getAll(STORES.ASYNC_OPS))
    .filter(
      (op) =>
        isOwnedBy(op, owner) &&
        op.batch_id &&
        (op.status === OP_STATUS.FAILED || op.status === OP_STATUS.BLOCKED) &&
        AUTO_RETRY_ERROR_TYPES.includes(op.error_type),
    )
    .sort(byCreatedAsc)
}

/**
 * Siapkan "Sinkronkan Sekarang": (1) op gagal UPLOAD milik `owner` → pending (reset
 * attempts & backoff); (2) op pending/sent yang masih menunggu jadwal dijadikan
 * jatuh tempo sekarang. Op gagal di BE ditangani terpisah (retryServerFailedOps).
 */
export const prepareManualFlush = async (owner) => {
  const db = await getLocalDb()
  const rows = (await db.getAll(STORES.ASYNC_OPS)).filter((op) => isOwnedBy(op, owner))
  const failedUploadIds = rows
    .filter((op) => op.status === OP_STATUS.FAILED && !op.batch_id)
    .map((op) => op.op_id)
  const waitingIds = rows
    .filter(
      (op) =>
        (op.status === OP_STATUS.PENDING || op.status === OP_STATUS.SENT) &&
        op.next_attempt_at != null,
    )
    .map((op) => op.op_id)
  await updateOps(failedUploadIds, () => ({
    status: OP_STATUS.PENDING,
    error: null,
    attempts: 0,
    next_attempt_at: null,
  }))
  await updateOps(waitingIds, () => ({ next_attempt_at: null }))
}

/** Jumlah op per status milik `owner` (pending/sent/failed/blocked). */
export const countByStatus = async (owner) => {
  const db = await getLocalDb()
  const rows = (await db.getAll(STORES.ASYNC_OPS)).filter((op) => isOwnedBy(op, owner))
  const out = { pending: 0, sent: 0, failed: 0, blocked: 0 }
  rows.forEach((op) => {
    if (op.status === OP_STATUS.PENDING) {
      out.pending += 1
    } else if (op.status === OP_STATUS.SENT) {
      out.sent += 1
    } else if (op.status === OP_STATUS.FAILED) {
      out.failed += 1
    } else if (op.status === OP_STATUS.BLOCKED) {
      out.blocked += 1
    }
  })
  return out
}

/** Jumlah op yang belum tuntas milik user lain di tenant yang sama (info Riwayat). */
export const countOtherUsersPending = async (owner) => {
  if (!owner) {
    return 0
  }
  const db = await getLocalDb()
  const rows = await db.getAll(STORES.ASYNC_OPS)
  return rows.filter(
    (op) =>
      String(op.endpoint ?? '') === String(owner.endpoint ?? '') &&
      String(op.user_id ?? '') !== String(owner.userId ?? '') &&
      (op.status === OP_STATUS.PENDING ||
        op.status === OP_STATUS.SENT ||
        op.status === OP_STATUS.FAILED ||
        op.status === OP_STATUS.BLOCKED),
  ).length
}
